<script setup lang="ts">
import type { AssociationMembershipResponse } from '~~/openapi-client/gen/schemas'

const props = withDefaults(defineProps<{
	members: AssociationMembershipResponse[]
	searchText?: string
	groupBy?: 'lastname' | 'system-user' | 'none'
	sortBy?: (a: AssociationMembershipResponse, b: AssociationMembershipResponse) => number
	noMembersText?: string
}>(), {
	groupBy: 'lastname',
	sortBy: (a, b) => a.lastname.localeCompare(b.lastname) || a.firstname.localeCompare(b.firstname),
})

const { isSystemUser } = useMembers()

/**
 * Sucht über die gesamte Liste, ob der Searchtext im Nachname, Vornamen oder Rolle vorkommt.
 */
const filteredMembers = computed(() => {
	if (!props.searchText) return props.members

	const search = props.searchText.toLowerCase()
	return props.members.filter((m) => {
		return m.lastname.toLowerCase().includes(search)
			|| m.firstname.toLowerCase().includes(search)
			|| m.tags.map(tag => tag.name.toLowerCase()).join(', ').includes(search)
	})
})

/**
 * Diese Daten Darstellung wird für den Mitgliederliste-Modal verwendet.
 * Die Mitglieder sind nach Nachnamen gruppiert und sortiert.
 * Der Suchtext beschränkt die Liste auf Mitglieder, deren Nachname, Vorname oder Rolle den Suchtext enthalten.
 */
const filteredMembersGroupedBy = computed(() => {
	const grouped: Record<string, AssociationMembershipResponse[]> = {}

	const sortedMembers = [...filteredMembers.value].sort(props.sortBy)

	switch (props.groupBy) {
		case 'lastname':
			sortedMembers.forEach((member) => {
				const firstLetter = member.lastname.charAt(0).toUpperCase()
				if (!grouped[firstLetter]) {
					grouped[firstLetter] = []
				}
				grouped[firstLetter].push(member)
			})
			break
		case 'system-user':
			sortedMembers.forEach((member) => {
				const key = isSystemUser(member) ? 'Mitgliedverwalter' : 'Mitglieder'
				if (!grouped[key]) {
					grouped[key] = []
				}
				grouped[key].push(member)
			})
			break
		case 'none':
			grouped[''] = sortedMembers
			break
		default:
			throw new Error(`Unknown groupBy value: ${props.groupBy}`)
	}

	return grouped
})
</script>

<template>
	<ion-list lines="full">
		<ion-item v-if="filteredMembers.length === 0">
			<ion-label>Keine Mitglieder gefunden.</ion-label>
		</ion-item>
		<template v-else>
			<template
				v-for="(memberGroup, key) in filteredMembersGroupedBy"
				:key="key"
			>
				<ion-item-divider
					v-if="groupBy !== 'none'"
					role="presentation"
				>
					<ion-label>{{ key }}</ion-label>
				</ion-item-divider>
				<template
					v-for="member in memberGroup"
					:key="member.id"
				>
					<slot
						name="item"
						v-bind="member"
					>
						<MemberListItem	:member="member" />
					</slot>
				</template>
			</template>
		</template>
	</ion-list>
</template>

<style scoped lang="scss">
ion-list {
	margin-bottom: 32px;
}

ion-item-divider {
	--background: var(--ion-color-grey-5);
	--inner-padding-top: 24px;
	--inner-padding-start: 24px;
	--inner-padding-bottom: 8px;
	--padding-start: 0;
}
</style>
